import React, { useEffect, useState, useRef } from 'react';

import {
  basisPointsToPercent,
  percentToBasisPoints,
} from 'features/deals/DealStages/EventActionCards/utils';
import { CashFlowModelingInputs } from 'features/drilldown/cashflows/CashFlowModelingInputs';
import { ChartSkeletonLoader } from 'features/pages/market-data/components/ChartSkeleton';
import { URLMapDeprecated } from 'features/pages/market-data/shared';

import { useQuery } from '@apollo/client';

import { StipulationFieldName, RateType } from '__generated__/globalTypes';
import { GetMarketData } from 'query/__generated__/getMarketData';
import { GET_MARKET_DATA } from 'query/getMarketData';

import { useBiddingPricingContext } from '../BiddingPricingContext';
import DownloadPriceYieldCurveButton from './DownloadPriceYieldCurveButton';
import MaturityData from './MaturityData';
import { YieldMatrixContentProps, YieldCurveData } from './types';
import {
  SOFR_YIELD_CURVE_FILE,
  TREASURY_YIELD_CURVE_FILE,
  lookupAtPricePercent,
  normalizePriceYieldMatrix,
  normalizeYieldCurveData,
  rateAsBps,
  rateAsPercent,
} from './utils';
import BidSummary, { BidSummaryInfo } from '../BidSummary/BidSummary';
import YieldToPriceChart from '../YieldToPriceChart';

export const YieldMatrixContent: React.FC<YieldMatrixContentProps> = ({
  selectedCarve,
  bidDetails,
  updateBidDetails,
  formValues,
  useSummaryData,
  onSubmit,
}) => {
  const [ycChartData, setYcChartData] = useState<YieldCurveData | null>(null);
  const { data } = useSummaryData(formValues);

  const chartParentRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(800);

  const [ycChartDataMap, setYcChartDataMap] = useState<URLMapDeprecated>({} as URLMapDeprecated);
  const ycChartDataUrl =
    formValues.rateType === RateType.RiskFree ? TREASURY_YIELD_CURVE_FILE : SOFR_YIELD_CURVE_FILE;

  const {
    data: marketData,
    loading: marketDataLoading,
    error: marketDataError,
  } = useQuery<GetMarketData>(GET_MARKET_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.marketData) {
        const urlMap: URLMapDeprecated = {};
        Object.entries(data.marketData.urlMap).forEach(([key, value]) => {
          urlMap[key] = String(value);
        });
        setYcChartDataMap(urlMap);
      }
    },
  });

  useEffect(() => {
    if (ycChartDataUrl) {
      fetch(ycChartDataMap[ycChartDataUrl]).then((response) => {
        response.json().then((data: YieldCurveData) => {
          setYcChartData(normalizeYieldCurveData(data, ycChartDataUrl));
        });
      });
    }
  }, [ycChartDataUrl, ycChartDataMap]);

  const carveSummary = selectedCarve?.carve_summary;

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartParentRef.current) {
        const parentWidth = chartParentRef.current.offsetWidth;
        setChartWidth(parentWidth - 310);
      }
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);

    return () => {
      window.removeEventListener('resize', updateChartWidth);
    };
  }, [chartParentRef]);

  if (!bidDetails || !carveSummary) return null;

  const bidBasisPoints = bidDetails?.carve?.stipulations?.bid_basis_points || 0;
  const bidPx = basisPointsToPercent(bidBasisPoints);

  const bidSummary: BidSummaryInfo = {
    purchaseDollarPrice: 0,
  };

  const rawMatrix =
    data != null && 'price_yield_matrix' in data.cashFlows
      ? data.cashFlows.price_yield_matrix
      : null;
  // Normalize dollar prices onto price-percent space once, using the carve
  // balance (cents). The bid and the chart share that space, so the lookup
  // below carries no balance arithmetic (the ENG-2915 trap).
  const yieldPoints = normalizePriceYieldMatrix(
    rawMatrix?.yield_matrix,
    carveSummary.unpaidBalance,
  );
  const spreadPoints = normalizePriceYieldMatrix(
    rawMatrix?.spread_matrix,
    carveSummary.unpaidBalance,
  );

  // The bid is already a price-percent.
  const bidPercent = parseFloat(basisPointsToPercent(bidBasisPoints));
  // Purchase price in cents for the summary: bid fraction × balance (cents).
  bidSummary.purchaseDollarPrice = (bidPercent / 100) * carveSummary.unpaidBalance;

  const yieldResult = lookupAtPricePercent(yieldPoints, bidPercent);
  if (yieldResult) {
    bidSummary.netYield = rateAsPercent(yieldResult.rate);
    bidSummary.yieldDuration = yieldResult.durationYears;
  }

  const spreadResult = lookupAtPricePercent(spreadPoints, bidPercent);
  if (spreadResult) {
    bidSummary.spread = rateAsBps(spreadResult.rate);
    bidSummary.spreadDuration = spreadResult.durationYears;
  }

  const handlePointClick = (pricePercent: number) => {
    const price = parseFloat(pricePercent.toFixed(3));
    const basisPoints = percentToBasisPoints(price.toString());

    updateBidDetails(
      {
        ...bidDetails,
        carve: {
          ...bidDetails.carve!,
          stipulations: {
            ...bidDetails.stipulations,
            bid_basis_points: basisPoints,
          },
        },
      },
      StipulationFieldName.bid_basis_points,
      basisPoints.toString(),
    );
  };

  return (
    <>
      <div
        style={{
          paddingBottom: 15,
          gap: 40,
          display: 'flex',
        }}
      >
        <CashFlowModelingInputs formValues={formValues} onSubmit={onSubmit} withServicingRate />
      </div>

      <MaturityData
        ycChartData={ycChartData}
        formValues={formValues}
        onCurveSelect={onSubmit}
        headerAction={
          <DownloadPriceYieldCurveButton
            yieldPoints={yieldPoints}
            spreadPoints={spreadPoints}
            formValues={formValues}
          />
        }
      />

      <div className="flex flex-col gap-8 md:flex-row" ref={chartParentRef}>
        <div style={{ width: `${chartWidth}px` }} className="w-full">
          {rawMatrix != null ? (
            <YieldToPriceChart
              yieldPoints={yieldPoints}
              spreadPoints={spreadPoints}
              bidPx={bidPx}
              width={chartWidth - 25}
              onPointClick={handlePointClick}
            />
          ) : (
            <ChartSkeletonLoader height={500} width={chartWidth - 25} />
          )}
        </div>

        <div className="min-w-[300px] pr-4">
          <BidSummary
            netCoupon={carveSummary.netCoupon}
            unpaidBalance={carveSummary.unpaidBalance}
            netYield={bidSummary.netYield}
            spread={bidSummary.spread}
            yieldDuration={bidSummary.yieldDuration}
            spreadDuration={bidSummary.spreadDuration}
            participationPercent={Number(bidDetails.participationPercent.value)}
            bidBasisPoints={bidDetails.carve?.stipulations?.bid_basis_points}
            servicingRate={Number(bidDetails.stipulations?.servicingRate.value)}
            defaultAssumptionType={bidDetails.defaultAssumptionType}
            cdr={bidDetails.cdr}
            prepaymentAssumptionType={bidDetails.prepaymentAssumptionType}
            cpr={bidDetails.cpr}
          />
        </div>
      </div>
    </>
  );
};

export default YieldMatrixContent;
